'use client';

import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Heart,
  MessageCircle,
  Bookmark,
  Share2,
  UserPlus,
  UserCheck,
  Loader2,
  Send,
  ThumbsUp,
  BookOpen,
  Smile,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { savePendingAction } from '@/lib/auth/pending-action';
import { toast } from 'sonner';

const REACTIONS = [
  { type: 'moved', label: 'Moved', Icon: Heart },
  { type: 'believe', label: 'Believe', Icon: ThumbsUp },
  { type: 'inspired', label: 'Inspired', Icon: BookOpen },
  { type: 'smile', label: 'Smile', Icon: Smile },
] as const;

export type PublicComment = {
  id: string;
  body: string;
  created_at: string;
  author?: { full_name?: string; public_handle?: string } | null;
};

type Props = {
  postId?: string;
  /** 'story_post' for story_posts table; 'community_post' (default) for community_posts table */
  postSource?: 'story_post' | 'community_post';
  heritageItemId?: string;
  institutionId?: string;
  authorId?: string;
  authorHandle?: string;
  shareUrl?: string;
  shareTitle?: string;
  saveTitle?: string;
  saveContent?: string;
  savePillar?: string;
  initialLikeCount?: number;
  initialLiked?: boolean;
  isFollowing?: boolean;
  compact?: boolean;
  /** Light surfaces (public profile) vs dark article reader */
  variant?: 'light' | 'dark';
  onCommentPosted?: (comment: PublicComment) => void;
};

export default function PublicContentActions({
  postId,
  postSource = 'community_post',
  heritageItemId,
  institutionId,
  authorId,
  authorHandle,
  shareUrl,
  shareTitle,
  saveTitle,
  saveContent,
  savePillar,
  initialLikeCount = 0,
  initialLiked = false,
  isFollowing = false,
  compact = false,
  variant = 'dark',
  onCommentPosted,
}: Props) {
  const { user } = useAuth();
  const router = useRouter();
  const [liked, setLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [following, setFollowing] = useState(isFollowing);
  const [activeReaction, setActiveReaction] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savingRef, setSavingRef] = useState(false);

  const isLight = variant === 'light';

  useEffect(() => {
    setLiked(initialLiked);
    setLikeCount(initialLikeCount);
  }, [initialLiked, initialLikeCount]);

  useEffect(() => {
    setFollowing(isFollowing);
  }, [isFollowing]);

  const requireAuth = useCallback(
    (type: 'like' | 'comment' | 'save' | 'follow' | 'witness') => {
      const returnTo =
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
      savePendingAction({
        type,
        postId,
        recordId: heritageItemId,
        targetUserId: institutionId || authorId,
        returnTo,
      });
      router.push(`/login?next=${encodeURIComponent(returnTo)}`);
    },
    [router, postId, heritageItemId, institutionId, authorId]
  );

  const toggleHeritageLike = async () => {
    if (!heritageItemId) return;
    if (!user) {
      requireAuth('like');
      return;
    }
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      const res = await fetch('/api/heritage/engage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: heritageItemId, action: next ? 'like' : 'unlike' }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setLiked(!next);
      setLikeCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error('Could not update like');
    }
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    if (!user) {
      requireAuth('comment');
      return;
    }
    setSubmitting(true);
    try {
      if (heritageItemId) {
        const res = await fetch('/api/heritage/engage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_id: heritageItemId, action: 'comment', body: text }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        setCommentText('');
        setCommentOpen(false);
        toast.success('Comment posted');
        if (json.comment) {
          onCommentPosted?.({
            id: json.comment.id,
            body: json.comment.body,
            created_at: json.comment.created_at,
          });
        }
      } else if (postId) {
        const res = await fetch('/api/community/comment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ post_id: postId, body: text, source_type: postSource }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        setCommentText('');
        setCommentOpen(false);
        toast.success('Comment posted');
        if (json.comment) {
          onCommentPosted?.({
            id: json.comment.id,
            body: json.comment.body,
            created_at: json.comment.created_at,
          });
        }
      }
    } catch {
      toast.error('Could not post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReact = async (type: string) => {
    if (!postId) return;
    if (!user) {
      requireAuth('witness');
      return;
    }
    const removing = activeReaction === type;
    setActiveReaction(removing ? null : type);
    try {
      const res = await fetch('/api/community/react', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: postId, reaction_type: type, source_type: postSource }),
      });
      if (!res.ok) throw new Error();
      const json = await res.json();
      if (json.action === 'removed') setActiveReaction(null);
      else setActiveReaction(type);
    } catch {
      setActiveReaction(activeReaction);
      toast.error('Could not save reaction');
    }
  };

  const handleFollow = async () => {
    const target = institutionId || authorId;
    if (!target) return;
    if (!user) {
      requireAuth('follow');
      return;
    }
    const next = !following;
    setFollowing(next);
    try {
      const res = await fetch('/api/community/follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_user_id: target,
          action: next ? 'follow' : 'unfollow',
        }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setFollowing(!next);
      toast.error('Could not update follow');
    }
  };

  const handleShare = async () => {
    const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const title = shareTitle || 'Ertzyx';
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      }
    } catch {
      /* cancelled */
    }
  };

  const handleSave = async () => {
    if (!user) {
      requireAuth('save');
      return;
    }
    setSavingRef(true);
    try {
      const res = await fetch('/api/ledger/save-public-record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reference',
          source_type: heritageItemId ? 'heritage_archive' : 'community_post',
          source_id: heritageItemId || postId,
          title: saveTitle || shareTitle || 'Public reference',
          content: saveContent || '',
          pillar: savePillar || 'History',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error);
      }
      toast.success('Saved to your Living Ledger');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not save reference');
    } finally {
      setSavingRef(false);
    }
  };

  const btnStyle: React.CSSProperties = isLight
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: compact ? '8px 12px' : '10px 14px',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        color: '#334155',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: compact ? '8px 12px' : '10px 14px',
        borderRadius: 10,
        border: '1px solid rgba(148,163,184,0.35)',
        background: 'rgba(255,255,255,0.06)',
        color: '#e2e8f0',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      };

  const canComment = !!(heritageItemId || postId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {heritageItemId && (
          <button
            type="button"
            onClick={toggleHeritageLike}
            style={{
              ...btnStyle,
              borderColor: liked ? 'rgba(236,72,153,0.5)' : (btnStyle.border as string),
              color: liked ? '#db2777' : btnStyle.color,
            }}
          >
            <Heart size={14} fill={liked ? 'currentColor' : 'none'} />
            {likeCount > 0 ? likeCount : 'Like'}
          </button>
        )}

        {postId &&
          REACTIONS.map((r) => (
            <button
              key={r.type}
              type="button"
              onClick={() => handleReact(r.type)}
              style={{
                ...btnStyle,
                opacity: activeReaction && activeReaction !== r.type ? 0.55 : 1,
                borderColor:
                  activeReaction === r.type ? 'rgba(99,102,241,0.45)' : (btnStyle.border as string),
              }}
            >
              <r.Icon size={14} aria-hidden />
              {r.label}
            </button>
          ))}

        {canComment && (
          <button
            type="button"
            onClick={() => (user ? setCommentOpen((o) => !o) : requireAuth('comment'))}
            style={btnStyle}
          >
            <MessageCircle size={14} />
            Comment
          </button>
        )}

        <button type="button" onClick={handleSave} disabled={savingRef} style={btnStyle}>
          {savingRef ? <Loader2 size={14} className="animate-spin" /> : <Bookmark size={14} />}
          Save
        </button>

        <button type="button" onClick={handleShare} style={btnStyle}>
          <Share2 size={14} />
          Share
        </button>

        {(institutionId || authorId) && (
          <button type="button" onClick={handleFollow} style={btnStyle}>
            {following ? <UserCheck size={14} /> : <UserPlus size={14} />}
            {following ? 'Following' : 'Follow'}
          </button>
        )}

        {authorHandle && (
          <Link href={`/u/${authorHandle}`} style={{ ...btnStyle, textDecoration: 'none' }}>
            View profile
          </Link>
        )}
      </div>

      {commentOpen && canComment && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a thoughtful comment…"
            rows={2}
            maxLength={280}
            style={{
              flex: 1,
              borderRadius: 10,
              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(148,163,184,0.35)',
              background: isLight ? '#f8fafc' : 'rgba(15,23,42,0.4)',
              color: isLight ? '#0f172a' : '#f1f5f9',
              padding: '10px 12px',
              fontSize: 13,
              resize: 'vertical',
            }}
          />
          <button
            type="button"
            onClick={submitComment}
            disabled={submitting || !commentText.trim()}
            style={{
              ...btnStyle,
              background: '#4f46e5',
              border: 'none',
              color: '#fff',
            }}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      )}
    </div>
  );
}
